export type Lang = "es" | "en";

type TFn = {
  nav: { home: string; about: string; services: string; blog: string; contact: string };
  hero: { title: string; subtitle: string; cta: string };
  why: { title: string; subtitle: string; items: { title: string; desc: string }[] };
  about: {
    title: string; p1: string; p2: string;
    mission: { title: string; text: string };
    vision: { title: string; text: string };
  };
  services: { title: string; subtitle: string; more: string; items: { title: string; desc: string }[] };
  blog: { title: string; subtitle: string; read: string; back: string };
  contact: {
    title: string; subtitle: string;
    bullets: { t: string; d: string }[];
    form: {
      name: string; namePh: string; email: string; emailPh: string;
      company: string; companyPh: string; phone: string; phonePh: string;
      message: string; messagePh: string; interests: string;
      submit: string; success: string; contactInfo: string;
    };
  };
  footer: {
    tagline: string; nav: string; contact: string; follow: string;
    whyUs: string; rights: string; privacy: string;
  };
};

export const translations: Record<Lang, TFn> = {
  es: {
    nav: { home: "Inicio", about: "Nosotros", services: "Servicios", blog: "Blog", contact: "Contacto" },
    hero: {
      title: "Tu negocio, siempre activo.",
      subtitle:
        "En GAMO Consulting, no solo planeamos para la adversidad; diseñamos tu capacidad de prosperar a pesar de ella sin importar el tamaño de tu compañía.",
      cta: "Solicitar Diagnóstico Gratuito",
    },
    why: {
      title: "¿Por Qué Elegir GAMO Consulting?",
      subtitle:
        "Más que consultores, somos sus aliados estratégicos en la construcción de una organización verdaderamente resiliente.",
      items: [
        { title: "Planes Accionables", desc: "No solo documentos: entregamos estrategias prácticas y ejecutables que su equipo puede implementar inmediatamente." },
        { title: "Escalabilidad para Pymes", desc: "Soluciones adaptadas a empresas medianas y pequeñas, sin comprometer calidad ni efectividad." },
        { title: "Cultura de Resiliencia Integral", desc: "Transformamos la continuidad en parte del ADN organizacional, no solo un plan en papel." },
        { title: "Experiencia Comprobada", desc: "Años de experiencia en gestión de riesgos y continuidad de negocio en diversos sectores industriales." },
      ],
    },
    about: {
      title: "Quiénes Somos",
      p1: "GAMO Consulting es una firma especializada en Continuidad de Negocio y Resiliencia Organizacional. Acompañamos a empresas de todos los tamaños en la construcción de capacidades para anticipar, resistir y recuperarse ante cualquier disrupción.",
      p2: "Nuestro enfoque combina metodologías internacionales con la realidad operativa de cada organización, entregando soluciones prácticas que generan valor desde el primer día.",
      mission: { title: "Misión", text: "Fortalecer la capacidad operativa de nuestros clientes mediante estrategias accionables de continuidad y resiliencia." },
      vision: { title: "Visión", text: "Ser el aliado estratégico de referencia en resiliencia organizacional en Latinoamérica." },
    },
    services: {
      title: "Nuestros Servicios",
      subtitle: "Soluciones integrales de continuidad y resiliencia organizacional adaptadas a sus necesidades",
      more: "Más información",
      items: [
        { title: "Análisis de Impacto al Negocio (BIA)", desc: "Identificamos y priorizamos los procesos críticos de su negocio para determinar el impacto financiero y operativo de una interrupción." },
        { title: "Evaluación de Riesgos", desc: "Evaluamos las amenazas potenciales y vulnerabilidades de su entorno para implementar controles mitigadores efectivos y proactivos." },
        { title: "Planes de Continuidad del Negocio (BCP)", desc: "Desarrollamos estrategias integrales para asegurar que su organización pueda seguir operando y atendiendo a sus clientes durante una crisis." },
        { title: "Planes de Recuperación Tecnológica", desc: "Diseñamos planes DRP para restaurar la infraestructura tecnológica crítica en los tiempos objetivo definidos." },
        { title: "Pruebas y Capacitación", desc: "Validamos la efectividad de sus planes mediante ejercicios y capacitamos a su equipo para una respuesta segura." },
        { title: "Resiliencia Organizacional", desc: "Integramos la continuidad en la cultura, los procesos y el liderazgo de su organización." },
      ],
    },
    blog: {
      title: "Blog",
      subtitle: "Insights y mejores prácticas en gestión de riesgos y resiliencia",
      read: "Leer más",
      back: "Volver al Blog",
    },
    contact: {
      title: "Comience Hoy su Transformación hacia la Resiliencia",
      subtitle: "Solicite un diagnóstico gratuito y descubra cómo podemos fortalecer la continuidad de su negocio.",
      bullets: [
        { t: "Diagnóstico sin compromiso", d: "Evaluación inicial gratuita de sus necesidades" },
        { t: "Respuesta en 24 horas", d: "Nos pondremos en contacto rápidamente" },
        { t: "Soluciones personalizadas", d: "Adaptadas al tamaño y sector de su empresa" },
      ],
      form: {
        name: "Nombre Completo", namePh: "Juan Pérez",
        email: "Correo Corporativo", emailPh: "juan.perez@empresa.com",
        company: "Empresa", companyPh: "Nombre de su empresa",
        phone: "Teléfono", phonePh: "3001234567",
        message: "Mensaje (Opcional)", messagePh: "Cuéntenos un poco más sobre sus necesidades...",
        interests: "Servicios de Interés (Opcional)",
        submit: "Solicitar Diagnóstico Gratuito",
        success: "¡Gracias! Nos pondremos en contacto pronto.",
        contactInfo: "Información de contacto",
      },
    },
    footer: {
      tagline: "Protegiendo la operatividad de su empresa ante cualquier crisis.",
      nav: "Navegación",
      contact: "Contacto",
      follow: "Síguenos",
      whyUs: "¿Por Qué Elegir GAMO Consulting?",
      rights: "© 2026 GAMO Consulting. Todos los derechos reservados.",
      privacy: "Política de Privacidad",
    },
  },
  en: {
    nav: { home: "Home", about: "About", services: "Services", blog: "Blog", contact: "Contact" },
    hero: {
      title: "Your business, always running.",
      subtitle:
        "At GAMO Consulting, we don't just plan for adversity; we design your ability to thrive despite it, no matter the size of your company.",
      cta: "Request Free Assessment",
    },
    why: {
      title: "Why Choose GAMO Consulting?",
      subtitle: "More than consultants, we are your strategic allies in building a truly resilient organization.",
      items: [
        { title: "Actionable Plans", desc: "Not just documents: we deliver practical, executable strategies your team can implement immediately." },
        { title: "Scalable for SMBs", desc: "Solutions tailored to small and mid-sized companies without compromising quality or effectiveness." },
        { title: "Integral Resilience Culture", desc: "We turn continuity into part of your organizational DNA, not just a plan on paper." },
        { title: "Proven Experience", desc: "Years of experience in risk management and business continuity across multiple industries." },
      ],
    },
    about: {
      title: "Who We Are",
      p1: "GAMO Consulting is a firm specialized in Business Continuity and Organizational Resilience. We support companies of all sizes in building the capability to anticipate, withstand, and recover from any disruption.",
      p2: "Our approach combines international methodologies with the operational reality of each organization, delivering practical solutions that create value from day one.",
      mission: { title: "Mission", text: "Strengthen our clients' operational capacity through actionable continuity and resilience strategies." },
      vision: { title: "Vision", text: "Become the leading strategic ally for organizational resilience in Latin America." },
    },
    services: {
      title: "Our Services",
      subtitle: "Comprehensive continuity and organizational resilience solutions tailored to your needs",
      more: "Learn more",
      items: [
        { title: "Business Impact Analysis (BIA)", desc: "We identify and prioritize your business-critical processes to determine the financial and operational impact of a disruption." },
        { title: "Risk Assessment", desc: "We assess potential threats and vulnerabilities in your environment to implement effective, proactive mitigation controls." },
        { title: "Business Continuity Plans (BCP)", desc: "We develop integral strategies so your organization keeps operating and serving customers during a crisis." },
        { title: "Technology Recovery Plans", desc: "We design DRP plans to restore critical technology infrastructure within defined recovery objectives." },
        { title: "Testing & Training", desc: "We validate plan effectiveness through exercises and train your team for a confident response." },
        { title: "Organizational Resilience", desc: "We embed continuity into your culture, processes, and leadership." },
      ],
    },
    blog: {
      title: "Blog",
      subtitle: "Insights and best practices in risk management and resilience",
      read: "Read more",
      back: "Back to Blog",
    },
    contact: {
      title: "Start Your Resilience Transformation Today",
      subtitle: "Request a free assessment and discover how we can strengthen your business continuity.",
      bullets: [
        { t: "No-commitment assessment", d: "Free initial evaluation of your needs" },
        { t: "24-hour response", d: "We'll get back to you quickly" },
        { t: "Personalized solutions", d: "Tailored to your company's size and industry" },
      ],
      form: {
        name: "Full Name", namePh: "John Smith",
        email: "Corporate Email", emailPh: "john.smith@company.com",
        company: "Company", companyPh: "Your company name",
        phone: "Phone", phonePh: "3001234567",
        message: "Message (Optional)", messagePh: "Tell us a bit more about your needs...",
        interests: "Services of Interest (Optional)",
        submit: "Request Free Assessment",
        success: "Thank you! We'll be in touch soon.",
        contactInfo: "Contact information",
      },
    },
    footer: {
      tagline: "Protecting your company's operations against any crisis.",
      nav: "Navigation",
      contact: "Contact",
      follow: "Follow us",
      whyUs: "Why Choose GAMO Consulting?",
      rights: "© 2026 GAMO Consulting. All rights reserved.",
      privacy: "Privacy Policy",
    },
  },
} as const;

export type Dict = TFn;